import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import Navigation from "../components/layout/Navigation";
import { toast } from "sonner";
import { Mail, Lock, User, Building, ArrowRight, FileText } from "lucide-react";
import { trackSignup } from "../utils/analytics";

const RegisterPage = () => {
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get("role") || "buyer";
  const agentCode = searchParams.get("agent") || "";  // Sales agent code from QR
  const referralCode = searchParams.get("ref") || "";  // Promoter referral code

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(defaultRole);
  const [businessName, setBusinessName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [taxId, setTaxId] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (role === "wholesale" && !businessName) {
      toast.error("Business name is required for wholesale accounts");
      return;
    }
    
    setLoading(true);
    
    try {
      await register(email, password, name, role, {
        business_name: businessName,
        business_type: businessType,
        tax_id: taxId,
        agent_code: agentCode || undefined,  // Pass agent code if present
        referred_by: referralCode || undefined  // Pass referral code if present
      });
      
      // Track signup event
      trackSignup('email');
      
      if (role === "wholesale") {
        const message = agentCode 
          ? "Wholesale application submitted! You've been linked to your sales representative."
          : "Wholesale application submitted! We'll review it shortly.";
        toast.success(message);
        navigate("/dashboard");
      } else {
        toast.success("Account created successfully!");
        navigate(role === "promoter" ? "/promoter" : "/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="register-page">
      <Navigation />

      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <h1 className="text-4xl font-medium mb-2">Create Account</h1>
              <p className="text-muted-foreground">
                Join our community of art collectors and retailers
              </p>
            </div>

            {/* Google Sign In */}
            <Button
              variant="outline"
              className="w-full rounded-full py-6 mb-6"
              onClick={googleLogin}
              data-testid="google-register-btn"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>

            <div className="relative my-8">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-sm text-muted-foreground">
                or
              </span>
            </div>

            {/* Account Type Selector */}
            <div className="mb-6">
              <Label className="mb-3 block">Account Type</Label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setRole("buyer")}
                  className={`p-4 rounded-xl border-2 text-left transition-colors ${
                    role === "buyer"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground"
                  }`}
                  data-testid="role-buyer-btn"
                >
                  <User className="h-5 w-5 mb-2 text-primary" />
                  <p className="font-medium text-sm">Collector</p>
                  <p className="text-xs text-muted-foreground">Browse art</p>
                </button>
                <button
                  type="button"
                  onClick={() => setRole("wholesale")}
                  className={`p-4 rounded-xl border-2 text-left transition-colors ${
                    role === "wholesale"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground"
                  }`}
                  data-testid="role-wholesale-btn"
                >
                  <Building className="h-5 w-5 mb-2 text-primary" />
                  <p className="font-medium text-sm">Wholesale</p>
                  <p className="text-xs text-muted-foreground">Trade pricing</p>
                </button>
                <button
                  type="button"
                  onClick={() => setRole("promoter")}
                  className={`p-4 rounded-xl border-2 text-left transition-colors ${
                    role === "promoter"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground"
                  }`}
                  data-testid="role-promoter-btn"
                >
                  <FileText className="h-5 w-5 mb-2 text-primary" />
                  <p className="font-medium text-sm">Ambassador</p>
                  <p className="text-xs text-muted-foreground">Earn commission</p>
                </button>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 rounded-xl"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 rounded-xl"
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 rounded-xl"
                    required
                    minLength={6}
                    data-testid="register-password-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  At least 6 characters
                </p>
              </div>

              {/* Wholesale Fields */}
              {role === "wholesale" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4 pt-4 border-t border-border"
                >
                  <p className="text-sm font-medium text-primary">Wholesale Application</p>
                  
                  <div className="space-y-2">
                    <Label htmlFor="businessName">Business Name *</Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="businessName"
                        type="text"
                        placeholder="Your business name"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        className="pl-10 rounded-xl"
                        required={role === "wholesale"}
                        data-testid="register-business-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="businessType">Business Type</Label>
                    <Input
                      id="businessType"
                      type="text"
                      placeholder="e.g., Gallery, Retailer, Interior Design"
                      value={businessType}
                      onChange={(e) => setBusinessType(e.target.value)}
                      className="rounded-xl"
                      data-testid="register-business-type"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="taxId">Tax ID / ABN (Optional)</Label>
                    <Input
                      id="taxId"
                      type="text"
                      placeholder="Your tax identification number"
                      value={taxId}
                      onChange={(e) => setTaxId(e.target.value)}
                      className="rounded-xl"
                      data-testid="register-tax-id"
                    />
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Your application will be reviewed within 24-48 hours. Once approved, you'll receive your exclusive trade pricing based on your business profile.
                  </p>
                </motion.div>
              )}

              <Button
                type="submit"
                className="w-full rounded-full py-6 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? "Creating account..." : role === "wholesale" ? "Submit Application" : "Create Account"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-8">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RegisterPage;
